import { ServiceAgenda } from '../../../../models/sale.constants'
import { STEPS } from './Steps/types'

/**
 * Checks if the service is scheduled or agenda is valid
 *
 * @param agenda ServiceAgenda object
 * @returns boolean Whether or not the service is scheduled
 */
export const isServiceScheduled = (agenda?: ServiceAgenda) => {
  if (!agenda) {
    return false
  }

  if (Object.keys(agenda).length === 0 && agenda.constructor === Object) {
    return false
  }

  if (agenda.date === '' || agenda.minutesScheduled === 0) {
    return false
  }

  if (
    typeof agenda.date !== 'string' ||
    typeof agenda.minutesScheduled !== 'number'
  ) {
    return false
  }

  return true
}

interface GetActiveBreadcrumbProps {
  step: STEPS
  breadcrumbs: string[]
}

/**
 * Returns the active breadcrumb based on the current step
 * @param step  Current step
 * @param breadcrumbs Array of breadcrumbs
 *
 * @returns string Active breadcrumb
 */
export const getActiveBreadcrumb = ({
  step,
  breadcrumbs,
}: GetActiveBreadcrumbProps): string => {
  return breadcrumbs[step - 1]
}

/**
 * Compares two dates, ignoring the time portion of the date objects
 * @param date1  - the first date object
 * @param date2 - the second date object
 * @returns - 0 if the dates are equal, 1 if date1 is before date2, -1 if date1 is after date2
 */
export const compareDates = (date1: Date, date2: Date) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    throw new Error('Invalid Date object')
  }

  const date1WithoutTime = new Date(
    date1.getFullYear(),
    date1.getMonth(),
    date1.getDate()
  )
  const date2WithoutTime = new Date(
    date2.getFullYear(),
    date2.getMonth(),
    date2.getDate()
  )

  // Compare the date-only objects
  if (date1WithoutTime < date2WithoutTime) {
    return 1 // date1 is before date2
  }
  if (date1WithoutTime > date2WithoutTime) {
    return -1 // date1 is after date2
  }
  return 0 // date1 is equal to date2
}

// If the DatePicker is displayed and the service has a date,
// it is possible that the date is outside the 14-day range shown from today.
// Adjust the table of dates accordingly.
/**
 * Get the initial start date for the date range displayed on the DatePicker based on the service date and the feature flag.
 * @param initialSelectedDate - the initial selected date from the service
 * @param startDate - the start date for the date range set on the DatePicker change
 * @returns - the initial start date for the date range
 */
export const getInitialStartDateForRange = ({
  initialSelectedDate,
  startDate = null,
}: {
  initialSelectedDate: string
  startDate: string | null
}) => {
  const todayDate = new Date(new Date().setHours(0, 0, 0, 0))

  if (startDate) {
    return startDate
  }
  // if the service has a date, but the user hasn't yet interacted with the date picker,
  // set the selected date so the selected date can be displayed at the top of the date table.
  if (
    initialSelectedDate &&
    compareDates(new Date(initialSelectedDate), todayDate) < 0 &&
    !startDate
  ) {
    return initialSelectedDate
  }
  return todayDate.toISOString()
}
